import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './PascaleRunProgress.scss';

export const PASCALE_STEPS_FULL = [
  'connect',
  'accountingScan',
  'rulesCreate',
  'rulesMatch',
  'ivaPre',
  'traspasoReg',
  'nomina',
  'invoiceMovements',
  'strong',
  'strongMultiple',
  'bankCommission',
  'exact',
  'weakCombos',
  'ivaPost',
  'f29',
  'weakReinforcement',
  'credit',
  'entries',
];

export const PASCALE_STEPS_ACCOUNTING = [
  'connect',
  'accountingScan',
  'rules',
  'entries',
];

const SECTION_FOR = {
  connect: 'process',
  accountingScan: 'process',
  ivaPre: 'layers',
  traspasoReg: 'layers',
  nomina: 'layers',
  invoiceMovements: 'layers',
  strong: 'layers',
  strongMultiple: 'layers',
  bankCommission: 'layers',
  exact: 'layers',
  weakCombos: 'layers',
  ivaPost: 'layers',
  f29: 'layers',
  weakReinforcement: 'layers',
  credit: 'layers',
  rulesCreate: 'rules',
  rulesMatch: 'rules',
  rules: 'rules',
  entries: 'entries',
};

const STEP_MS = 1100;

function CheckIcon({ size = 10 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function PascaleRunProgress({
  companyName,
  running,
  outcome,
  error,
  collapsed,
  onCollapse,
  onExpand,
  onClose,
  variant = 'full',
  stepId,
}) {
  const { t } = useTranslation('header');
  const stepIds = variant === 'accounting' ? PASCALE_STEPS_ACCOUNTING : PASCALE_STEPS_FULL;
  const [step, setStep] = useState(0);
  const intervalRef = useRef(null);
  const useLiveStep = Boolean(stepId);

  useEffect(() => {
    if (useLiveStep) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const idx = stepIds.indexOf(stepId);
      setStep(idx >= 0 ? idx : 0);
      return undefined;
    }
    if (!running) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return undefined;
    }
    setStep(0);
    intervalRef.current = setInterval(() => {
      setStep((current) => Math.min(current + 1, stepIds.length - 1));
    }, STEP_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, stepIds, stepId, useLiveStep]);

  if (!running && !outcome && !error) return null;

  const done = !running;
  const failed = Boolean(error);
  const matched = outcome?.matched ?? 0;
  const created = outcome?.created ?? 0;
  const jobs = outcome?.processedBatches ?? 0;
  const currentId = stepIds[Math.min(step, stepIds.length - 1)];
  const prevId = step > 0 ? stepIds[step - 1] : null;
  const sectionKey = SECTION_FOR[currentId];
  const summaryLine = failed
    ? error
    : `${t('pascale.progress.summaryLayers', { matched, created })}${
        jobs > 0 ? ` · ${t('pascale.progress.summaryJobs', { jobs })}` : ''
      }`;

  if (collapsed) {
    return createPortal(
      <button
        type="button"
        className={`pascale-run-chip${running ? ' is-running' : ''}${failed ? ' is-failed' : ''}`}
        onClick={onExpand}
        aria-label={t('pascale.progress.reopen')}
      >
        <span className={`pascale-run-chip-dot${running ? ' is-spin' : ''}`} />
        <span>
          {running
            ? `${t('pascale.progress.chipRunning')}${companyName ? ` · ${companyName}` : ''}`
            : failed
              ? t('pascale.progress.chipFailed')
              : t('pascale.progress.chipDone')}
        </span>
      </button>,
      document.body,
    );
  }

  return createPortal(
    <div
      className={`pascale-run-panel${done ? ' is-done' : ''}`}
      role="status"
      aria-label={t('pascale.progress.aria')}
      style={{ width: done ? 300 : 280 }}
    >
      <div className="pascale-run-panel-head">
        <div>
          <p className="pascale-run-kicker">
            {done
              ? (failed ? t('pascale.progress.kickerFailed') : t('pascale.progress.kickerDone'))
              : t('pascale.progress.kickerRunning')}
          </p>
          {!done ? (
            <h2 className="pascale-run-company">{companyName || t('pascale.run')}</h2>
          ) : null}
        </div>
        <div className="pascale-run-head-actions">
          <button type="button" className="pascale-run-icon-btn" onClick={onCollapse} title={t('pascale.progress.minimize')}>
            –
          </button>
          {done ? (
            <button type="button" className="pascale-run-icon-btn" onClick={onClose} title={t('pascale.progress.close')}>
              ×
            </button>
          ) : null}
        </div>
      </div>

      {!done ? (
        <>
          {sectionKey ? (
            <p className="pascale-run-section">{t(`pascale.progress.sections.${sectionKey}`)}</p>
          ) : null}
          <ol className="pascale-run-steps">
            {prevId ? (
              <li className="pascale-run-step is-complete">
                <span className="pascale-run-dot"><CheckIcon /></span>
                <span>{t(`pascale.progress.steps.${prevId}`)}</span>
              </li>
            ) : null}
            <li className="pascale-run-step is-active">
              <span className="pascale-run-dot">
                <span className="pascale-run-dot-spin" />
              </span>
              <span>{t(`pascale.progress.steps.${currentId}`)}</span>
            </li>
          </ol>
        </>
      ) : (
        <div className="pascale-run-summary">
          {failed ? (
            <p className="pascale-run-error">{error}</p>
          ) : (
            <>
              <p>{summaryLine}</p>
              {outcome?.message ? (
                <p className="pascale-run-tech">{outcome.message}</p>
              ) : null}
              {Array.isArray(outcome?.errors) && outcome.errors.length ? (
                <ul className="pascale-run-notes">
                  {outcome.errors.slice(0, 3).map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
}
